import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/date";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Pencil, Plus, Trash2, Wallet, X } from "lucide-react";
import DateInput from "@/components/DateInput";

const MONTHS = ["Ianuarie", "Februarie", "Martie", "Aprilie", "Mai", "Iunie", "Iulie", "August", "Septembrie", "Octombrie", "Noiembrie", "Decembrie"];
const pad = (n) => String(n).padStart(2, "0");
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const curMonth = () => today().slice(0, 7);
const shiftMonth = (m, delta) => { const [y, mo] = m.split("-").map(Number); const d = new Date(y, mo - 1 + delta, 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };
const monthLabel = (m) => { const [y, mo] = m.split("-").map(Number); return `${MONTHS[mo - 1]} ${y}`; };
const lei = (v) => `${Number(v || 0).toLocaleString("ro-RO", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} lei`;
const EMPTY = { employee_name: "", amount: "", description: "", date: "" };
const inputCls = "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0047FF]";

function ExpenseForm({ employees, editing, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY, date: today() });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(editing ? { ...editing, amount: String(editing.amount) } : { ...EMPTY, date: today() }); }, [editing]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  async function submit(e) {
    e.preventDefault();
    const amount = parseFloat(String(form.amount).replace(",", "."));
    if (!form.employee_name.trim() || !form.description.trim() || !form.date || !(amount > 0)) {
      toast.error("Completează angajat, sumă, ce a cumpărat și data");
      return;
    }
    setSaving(true);
    try {
      const body = { employee_name: form.employee_name.trim(), amount, description: form.description.trim(), date: form.date };
      if (editing) await api.put(`/employee-expenses/${editing.id}`, body);
      else await api.post("/employee-expenses", body);
      toast.success(editing ? "Cheltuială actualizată" : "Cheltuială adăugată");
      if (!editing) setForm((f) => ({ ...EMPTY, employee_name: f.employee_name, date: f.date }));
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Salvarea a eșuat");
    } finally { setSaving(false); }
  }

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-4 grid grid-cols-2 sm:grid-cols-[1.2fr_0.8fr_2fr_1fr_auto] gap-2 items-end" data-testid="emp-expense-form">
      <div className="col-span-1">
        <label className="text-[10px] uppercase tracking-widest text-slate-400">Angajat</label>
        <input list="emp-names" value={form.employee_name} onChange={set("employee_name")} placeholder="Nume angajat" className={inputCls} data-testid="emp-expense-name-input" />
        <datalist id="emp-names">{employees.map((n) => <option key={n} value={n} />)}</datalist>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-slate-400">Sumă (lei)</label>
        <input inputMode="decimal" value={form.amount} onChange={set("amount")} placeholder="250" className={`${inputCls} tabular`} data-testid="emp-expense-amount-input" />
      </div>
      <div className="col-span-2 sm:col-span-1">
        <label className="text-[10px] uppercase tracking-widest text-slate-400">Ce a cumpărat</label>
        <input value={form.description} onChange={set("description")} placeholder="ex. Alimentare motorină" className={inputCls} data-testid="emp-expense-desc-input" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-slate-400">Data</label>
        <DateInput value={form.date} onChange={(v) => setForm((f) => ({ ...f, date: v }))} testid="emp-expense-date-input" />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="emp-expense-submit-btn" className="inline-flex items-center justify-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full bg-[#0A0F1C] text-white hover:bg-slate-800 transition-colors disabled:opacity-60 whitespace-nowrap">
          {editing ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {editing ? "Salvează" : "Adaugă"}
        </button>
        {editing && (
          <button type="button" onClick={onCancel} data-testid="emp-expense-cancel-edit-btn" className="p-2 rounded-full border border-slate-200 hover:border-slate-400 transition-colors"><X className="w-4 h-4" /></button>
        )}
      </div>
    </form>
  );
}

function ExpenseRow({ e, onEdit, onDelete }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`emp-expense-row-${e.id}`}>
      <div className="w-20 shrink-0 text-xs text-slate-500 tabular">{formatDate(e.date)}</div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{e.description}</div>
        <div className="text-xs text-slate-500 truncate">{e.employee_name}</div>
      </div>
      <div className="text-sm font-semibold tabular shrink-0" data-testid={`emp-expense-amount-${e.id}`}>{lei(e.amount)}</div>
      <button onClick={() => onEdit(e)} data-testid={`emp-expense-edit-${e.id}`} className="p-1.5 text-slate-400 hover:text-slate-900 transition-colors"><Pencil className="w-4 h-4" /></button>
      <button onClick={() => onDelete(e)} data-testid={`emp-expense-delete-${e.id}`} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

export default function EmployeeExpenses() {
  const [items, setItems] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [month, setMonth] = useState(curMonth());
  const [employee, setEmployee] = useState("");
  const [editing, setEditing] = useState(null);

  async function load() {
    const params = { month: month || undefined, employee: employee || undefined };
    const [list, emps] = await Promise.all([api.get("/employee-expenses", { params }), api.get("/employee-expenses/employees")]);
    setItems(list.data);
    setEmployees(emps.data);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month, employee]);

  async function remove(e) {
    if (!window.confirm(`Ștergi „${e.description}” (${lei(e.amount)})?`)) return;
    await api.delete(`/employee-expenses/${e.id}`);
    toast.success("Cheltuială ștearsă");
    if (editing?.id === e.id) setEditing(null);
    load();
  }

  const total = (items || []).reduce((s, e) => s + (e.amount || 0), 0);
  const byEmployee = Object.entries((items || []).reduce((acc, e) => ({ ...acc, [e.employee_name]: (acc[e.employee_name] || 0) + e.amount }), {})).sort((a, b) => b[1] - a[1]);

  return (
    <div className="sm:pl-52 space-y-4" data-testid="employee-expenses-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Jurnal intern · doar administrator</div>
        <h1 className="font-display text-3xl tracking-tight font-medium">Cheltuieli angajați</h1>
      </div>

      <ExpenseForm employees={employees} editing={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center rounded-full border border-slate-200 bg-white">
          <button onClick={() => setMonth(shiftMonth(month || curMonth(), -1))} data-testid="emp-month-prev" className="p-1.5 hover:bg-slate-50 rounded-full"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium px-2 min-w-[120px] text-center" data-testid="emp-month-label">{month ? monthLabel(month) : "Toate lunile"}</span>
          <button onClick={() => setMonth(shiftMonth(month || curMonth(), 1))} data-testid="emp-month-next" className="p-1.5 hover:bg-slate-50 rounded-full"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <button onClick={() => setMonth(month ? "" : curMonth())} data-testid="emp-month-all-toggle" className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${!month ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>Toate lunile</button>
        <select value={employee} onChange={(e) => setEmployee(e.target.value)} data-testid="emp-filter-select" className="text-sm border border-slate-200 rounded-full px-3 py-1.5 bg-white">
          <option value="">Toți angajații</option>
          {employees.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <div className="ml-auto text-sm tabular" data-testid="emp-expenses-total">Total: <strong>{lei(total)}</strong></div>
      </div>

      {byEmployee.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="emp-expenses-breakdown">
          {byEmployee.map(([n, v]) => (
            <span key={n} className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-700">{n}: <strong className="tabular">{lei(v)}</strong></span>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="emp-expenses-list">
        {items === null && <div className="p-6 text-sm text-slate-400">Se încarcă…</div>}
        {items?.length === 0 && (
          <div className="p-10 text-center" data-testid="emp-expenses-empty">
            <Wallet className="w-10 h-10 mx-auto text-slate-300" />
            <div className="mt-3 font-display text-lg">Nicio cheltuială</div>
            <div className="text-sm text-slate-500 mt-1">Adaugă prima cheltuială a unui angajat mai sus.</div>
          </div>
        )}
        {items?.map((e) => <ExpenseRow key={e.id} e={e} onEdit={setEditing} onDelete={remove} />)}
      </div>
    </div>
  );
}
